import { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    Button,
    Typography,
    Box,
    LinearProgress,
} from '@mui/material';
import { MdCheckCircle, MdCancel, MdRocketLaunch } from 'react-icons/md';
import { ProvisionResult } from '../../services/tipos';

interface LogAtivacaoProps {
    open: boolean;
    loading: boolean;
    provisionResult: ProvisionResult | null;
    onClose: () => void;
    onRetry: () => void;
}

const STEP_LABELS = [
    'Criando acesso no LocaSmartCheck',
    'Importando veículos',
    'Importando motoristas',
    'Vinculando motoristas aos veículos',
    'Criando modelos de vistoria',
    'Configurando acesso dos motoristas no app',
    'Atualizando assinatura',
    'Finalizando integração',
];

function LogAtivacao({ open, loading, provisionResult, onClose, onRetry }: LogAtivacaoProps) {
    const [animatedStepIndex, setAnimatedStepIndex] = useState(-1);
    const [animationComplete, setAnimationComplete] = useState(false);

    useEffect(() => {
        if (provisionResult && provisionResult.steps && provisionResult.steps.length > 0) {
            setAnimatedStepIndex(-1);
            setAnimationComplete(false);

            const totalSteps = provisionResult.steps.length;
            let currentStep = 0;

            const interval = setInterval(() => {
                setAnimatedStepIndex(currentStep);
                currentStep++;
                if (currentStep >= totalSteps) {
                    clearInterval(interval);
                    setTimeout(() => setAnimationComplete(true), 500);
                }
            }, 400);

            return () => clearInterval(interval);
        }
    }, [provisionResult]);

    useEffect(() => {
        if (loading) {
            setAnimatedStepIndex(-1);
            setAnimationComplete(false);
        }
    }, [loading]);

    const isSuccess = provisionResult?.success === true;
    const canClose = !loading && animationComplete;

    return (
        <Dialog
            open={open}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={!canClose}
            onClose={(_event, reason) => {
                if (reason === 'backdropClick' && !canClose) return;
                if (canClose) onClose();
            }}
            PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
            {/* Header */}
            <Box sx={{
                background: 'linear-gradient(135deg, #0a1929 0%, #132f4c 100%)',
                color: 'white',
                p: 3,
                textAlign: 'center',
            }}>
                {loading && (
                    <>
                        <MdRocketLaunch style={{ fontSize: 40, marginBottom: 8 }} />
                        <Typography variant="h5" fontWeight="800">
                            Ativando LocaSmartCheck
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                            Aguarde enquanto configuramos tudo para você...
                        </Typography>
                    </>
                )}
                {!loading && animationComplete && isSuccess && (
                    <>
                        <MdCheckCircle style={{ fontSize: 48, color: '#66bb6a', marginBottom: 8 }} />
                        <Typography variant="h5" fontWeight="800">
                            Integração concluída!
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                            Seu LocaSmartCheck está pronto para uso
                        </Typography>
                    </>
                )}
                {!loading && animationComplete && !isSuccess && (
                    <>
                        <MdCancel style={{ fontSize: 48, color: '#ef5350', marginBottom: 8 }} />
                        <Typography variant="h5" fontWeight="800">
                            Falha na ativação
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                            {provisionResult?.errorMessage || 'Ocorreu um erro inesperado'}
                        </Typography>
                    </>
                )}
                {!loading && !animationComplete && (
                    <>
                        <MdRocketLaunch style={{ fontSize: 40, marginBottom: 8 }} />
                        <Typography variant="h5" fontWeight="800">
                            Processando...
                        </Typography>
                    </>
                )}
            </Box>

            {loading && <LinearProgress sx={{ height: 3 }} />}

            <DialogContent sx={{ p: 3 }}>
                {/* Steps */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {STEP_LABELS.map((label, index) => {
                        const step = provisionResult?.steps?.[index];
                        const isRevealed = loading || index <= animatedStepIndex;
                        const isCurrentlyAnimating = loading && !provisionResult;
                        const stepSuccess = step && (step.status === 'Success' || step.status === 'Skipped');
                        const stepError = step && step.status === 'Error';

                        return (
                            <Box key={index} sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 1.2,
                                borderRadius: 1.5,
                                backgroundColor: isRevealed
                                    ? (stepError ? '#ffebee' : stepSuccess ? '#e8f5e9' : '#f5f5f5')
                                    : '#fafafa',
                                border: '1px solid',
                                borderColor: isRevealed
                                    ? (stepError ? '#ffcdd2' : stepSuccess ? '#c8e6c9' : '#e0e0e0')
                                    : '#f0f0f0',
                                opacity: isRevealed ? 1 : 0.4,
                                transition: 'all 0.3s ease',
                                transform: isRevealed ? 'translateX(0)' : 'translateX(10px)',
                            }}>
                                {isRevealed && stepSuccess && (
                                    <MdCheckCircle style={{ color: '#4caf50', fontSize: 22, flexShrink: 0 }} />
                                )}
                                {isRevealed && stepError && (
                                    <MdCancel style={{ color: '#f44336', fontSize: 22, flexShrink: 0 }} />
                                )}
                                {(!isRevealed || isCurrentlyAnimating || (!step)) && (
                                    <Box sx={{
                                        width: 22, height: 22, borderRadius: '50%',
                                        border: '2px solid #e0e0e0', flexShrink: 0,
                                        ...(isCurrentlyAnimating && {
                                            borderColor: '#132f4c',
                                            borderTopColor: 'transparent',
                                            animation: 'spin 1s linear infinite',
                                            '@keyframes spin': {
                                                '0%': { transform: 'rotate(0deg)' },
                                                '100%': { transform: 'rotate(360deg)' },
                                            },
                                        }),
                                    }} />
                                )}
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" fontWeight={isRevealed ? 500 : 400}
                                        sx={{ color: isRevealed ? 'text.primary' : 'text.disabled' }}>
                                        {label}
                                    </Typography>
                                    {isRevealed && step?.message && (
                                        <Typography variant="caption"
                                            sx={{ color: stepError ? '#d32f2f' : 'text.secondary' }}>
                                            {step.message}
                                        </Typography>
                                    )}
                                </Box>
                                {isRevealed && step?.count !== undefined && step.count > 0 && (
                                    <Box sx={{
                                        backgroundColor: stepSuccess ? '#4caf50' : '#f44336',
                                        color: 'white',
                                        borderRadius: 1,
                                        px: 1,
                                        py: 0.2,
                                        fontSize: 12,
                                        fontWeight: 600,
                                    }}>
                                        {step.count}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>

                {/* CTA */}
                {canClose && (
                    <Box sx={{ textAlign: 'center', mt: 3 }}>
                        {isSuccess ? (
                            <Button
                                variant="contained"
                                size="large"
                                onClick={onClose}
                                sx={{
                                    px: 5,
                                    py: 1.5,
                                    fontWeight: 700,
                                    borderRadius: 2,
                                    background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)',
                                    boxShadow: '0 4px 15px rgba(46, 125, 50, 0.4)',
                                }}
                            >
                                Começar a usar
                            </Button>
                        ) : (
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                <Button variant="outlined" onClick={onClose}>
                                    Fechar
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={onRetry}
                                    sx={{
                                        background: 'linear-gradient(135deg, #0a1929 0%, #1a3a5c 100%)',
                                    }}
                                >
                                    Tentar novamente
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default LogAtivacao;
